"use client";

import { useState } from "react";
import { ExternalLink, Award, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslations } from "next-intl";
import { GLASS_CARD } from "@/components/glass-card";
import { cn } from "@/lib/utils";
import { MarkdownRenderer } from "@/components/markdown-renderer";
import type { components } from "@/openapi/generated";

type CertificateGrantDto = components["schemas"]["CertificateGrantDto"];

const DESCRIPTION_COLLAPSE_THRESHOLD = 120;

interface CertificateGrantCardProps {
  grant: CertificateGrantDto;
}

export function CertificateGrantCard({ grant }: CertificateGrantCardProps) {
  const t = useTranslations("projects");
  const [isExpanded, setIsExpanded] = useState(false);

  const OVERHEAD_COST_LABELS: Record<string, string> = {
    LOW: t("certificateGrant.overheadCostLow"),
    ACCEPTABLE: t("certificateGrant.overheadCostAcceptable"),
    APPROPRIATE: t("certificateGrant.overheadCostAppropriate"),
  };

  const grantedYear = grant.grantedAt
    ? new Date(grant.grantedAt).getFullYear()
    : null;

  const isLongDescription =
    !!grant.description &&
    grant.description.length > DESCRIPTION_COLLAPSE_THRESHOLD;

  return (
    <div className={cn(GLASS_CARD, "border-primary/20")}>
      <div className="flex items-start gap-3">
        <Award className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-foreground">
              {grant.certificateTitle ?? t("certificateGrant.defaultTitle")}
            </span>
            {grantedYear && (
              <span className="text-xs text-muted-foreground">{t("certificateGrant.since", { year: grantedYear })}</span>
            )}
            {grant.overheadCosts && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                {OVERHEAD_COST_LABELS[grant.overheadCosts] ?? grant.overheadCosts}
              </span>
            )}
          </div>
          {grant.description && (
            <>
              <div className={isLongDescription && !isExpanded ? "mt-3 line-clamp-3" : "mt-3"}>
                <MarkdownRenderer
                  content={grant.description}
                  className="text-muted-foreground [&_p]:text-muted-foreground"
                />
              </div>
              {isLongDescription && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="mt-2 inline-flex items-center gap-0.5 text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  {isExpanded ? (
                    <>{t("certificateGrant.showLess")} <ChevronUp className="h-3 w-3" /></>
                  ) : (
                    <>{t("certificateGrant.showMore")} <ChevronDown className="h-3 w-3" /></>
                  )}
                </button>
              )}
            </>
          )}
          {grant.certificateUrl && (
            <a
              href={grant.certificateUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 ml-6 mt-2 text-xs text-primary hover:text-primary/80 transition-colors"
            >
              {t("certificateGrant.viewCertificate")}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
